"use client";

import { useEffect, useRef, useState, type FC, type MouseEvent, type PointerEvent, type ReactNode } from "react";
import { cn } from "@/lib/cn";

type HorizontalScrollProps = { className?: string; children: ReactNode };

type DragState = { startX: number; startScroll: number; moved: boolean };

const DRAG_THRESHOLD_PX = 4;

export const HorizontalScroll: FC<HorizontalScrollProps> = ({ className, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef<DragState | null>(null);
  const suppressClick = useRef(false);
  const [canScroll, setCanScroll] = useState(false);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setCanScroll(el.scrollWidth > el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el || event.pointerType !== "mouse" || event.button !== 0 || el.scrollWidth <= el.clientWidth) return;
    event.preventDefault();
    event.stopPropagation();
    suppressClick.current = false;
    drag.current = { startX: event.clientX, startScroll: el.scrollLeft, moved: false };
    try {
      el.setPointerCapture(event.pointerId);
    } catch {}
    setDragging(true);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const el = ref.current;
    const state = drag.current;
    if (!el || !state) return;
    const delta = event.clientX - state.startX;
    if (Math.abs(delta) > DRAG_THRESHOLD_PX) state.moved = true;
    el.scrollLeft = state.startScroll - delta;
  };

  const endDrag = (event: PointerEvent<HTMLDivElement>) => {
    const el = ref.current;
    const state = drag.current;
    if (!el || !state) return;
    suppressClick.current = state.moved;
    drag.current = null;
    if (el.hasPointerCapture(event.pointerId)) el.releasePointerCapture(event.pointerId);
    setDragging(false);
  };

  const onClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (!suppressClick.current) return;
    suppressClick.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClickCapture={onClickCapture}
      className={cn(
        "overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        dragging ? "cursor-grabbing select-none" : canScroll && "[@media(pointer:fine)]:cursor-grab",
        className,
      )}
    >
      {children}
    </div>
  );
};
